// TrustModal.tsx — the ONE shared "Trust & provenance" overlay (Tier-1/2a).
// CONTRACT:C4-DESIGN-TOKENS.1.0
// Spec: Tak-TDF COP.dc.html:565-599 + the tdfSections data at :970-985.
//
// THE CRYPTOGRAPHIC BASEMENT. A centered, browser-lock-style overlay (z62, 520px,
// backdrop blur) that surfaces — and EXCLUSIVELY OWNS — the KAS verdict (rule 4):
// the GRANT/DENY verdict pill (KEY RELEASED / KEY NOT HELD) plus four sections
// (CLASSIFICATION / KEY ACCESS·KAS / ENCRYPTION / PROVENANCE) as key/value rows.
// Opened by BOTH the masthead "i" button AND the grey lock in the inspector's
// PROTECTED CONTENT addendum (rule 5: one shared `tdfOpen`).
//
// State (open/close) lives in App.tsx; this component is a pure projection over the
// immutable CopStatus snapshot (the selected envelope + status.decrypt). It reads
// REAL envelope/decrypt fields and renders "—" when a field is absent — it invents
// no host seams and reaches no engine. It does NOT touch the PDP: the verdict it
// shows is the host's already-computed decrypt state, not a new decision.
//
// GLOMAR: renders only over `status.selected` (an already-cleared envelope). The
// banners it shows carry CLASSIFICATION ONLY — no identity (banner purity).

import type { CopStatus } from "../host/cop.ts";
import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import { bannerLine, classificationLabel } from "./dod-marking.ts";
import { markingToHighWater } from "./payloadParse.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./trust-modal.css";

/** A single key/value row in a Trust section; `tone` keys the value's color. */
interface TrustRow {
  k: string;
  v: string;
  tone?: "grant" | "deny" | "dim" | "default";
}
interface TrustSection {
  title: string;
  rows: TrustRow[];
}

/** A defensive string reader: returns the value or an em-dash placeholder. */
function dash(v: unknown): string {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  return s.length > 0 ? s : "—";
}

/** Build the four spec sections from REAL envelope + decrypt fields. Missing
 *  fields render as "—" — no fabricated host seams. */
function buildSections(env: CanonicalEnvelope, decrypt: CopStatus["decrypt"]): TrustSection[] {
  const p = (env.properties ?? {}) as Record<string, unknown>;
  const sec = env.security;
  const granted = decrypt.state === "granted";
  // The protected (inner) marking, if the KAS released it; else the outer floor.
  // A RELEASED inner marking renders its FULL CAPCO line (bannerLine) — this is a
  // key/value row, not a banner, so banner purity (classification-only banners)
  // is untouched. Sealed content keeps the outer classification floor.
  const innerClass = granted && decrypt.marking
    ? bannerLine(markingToHighWater(decrypt.marking))
    : classificationLabel(sec.classification);
  const rel = Array.isArray(sec.releasability) && sec.releasability.length > 0
    ? sec.releasability.join(", ")
    : "(open)";
  const reqAttr = `clearance ≥ ${classificationLabel(sec.classification)}`
    + (Array.isArray(sec.mission) && sec.mission.length ? ` · ${sec.mission.join("/")}` : "")
    + (Array.isArray(sec.releasability) && sec.releasability.length ? ` · REL ${sec.releasability.join("/")}` : "");

  // A CLEAR-SHELL object (no encrypted payload) was never sealed and never sent to
  // the KAS, so the KEY ACCESS / ENCRYPTION sections have nothing real to report —
  // emitting them with "—" dashes falsely implies a failed decrypt. Gate them on
  // encryption; otherwise surface a single honest PROTECTION row. (undefined/absent
  // payload_mode is treated as clear.)
  const isEncrypted = sec.payload_mode === "encrypted";

  return [
    {
      title: "CLASSIFICATION",
      rows: [
        { k: "cleartext envelope", v: classificationLabel(sec.classification), tone: "default" },
        { k: "protected content", v: innerClass, tone: granted ? "default" : "deny" },
      ],
    },
    ...(isEncrypted
      ? ([
          {
            title: "KEY ACCESS · KAS",
            rows: [
              { k: "kas endpoint", v: dash((p.kas_url as string) ?? (p.real_kas === true ? "live KAS" : undefined)), tone: "default" },
              {
                k: "decision",
                // "unavailable" is a NO-DECISION outcome (auth/transport upstream of the ABAC
                // engine — e.g. an expired session token mid-refresh). It is NOT a DENY: the
                // content stays sealed, and reopening this modal retries the rewrap.
                v: granted
                  ? "GRANT · key released"
                  : decrypt.state === "denied"
                    ? "DENY · key withheld"
                    : decrypt.state === "unavailable"
                      ? "NO DECISION · key service unavailable — reopen to retry"
                      : "—",
                tone: granted ? "grant" : decrypt.state === "denied" ? "deny" : "dim",
              },
              { k: "required attrs", v: reqAttr, tone: "dim" },
            ],
          },
          {
            title: "ENCRYPTION",
            rows: [
              { k: "scheme", v: "TDFLite · ZTDF", tone: "default" },
              { k: "cipher", v: "AES-256-GCM", tone: "default" },
              { k: "key id", v: dash(p.key_id ?? p.kas_key), tone: "dim" },
            ],
          },
        ] as TrustSection[])
      : ([
          {
            title: "PROTECTION",
            rows: [
              { k: "scheme", v: "Clear payload — no cryptographic seal", tone: "dim" },
            ],
          },
        ] as TrustSection[])),
    {
      title: "PROVENANCE",
      rows: [
        { k: "original source", v: dash((p.source as string) ?? env.provenance?.originator), tone: "default" },
        { k: "data owner", v: dash(p.data_owner ?? env.provenance?.originator), tone: "default" },
        { k: "policy ref", v: dash(sec.policy_ref), tone: "dim" },
        { k: "signature", v: env.provenance?.chain?.length ? "valid ✓" : "—", tone: env.provenance?.chain?.length ? "grant" : "dim" },
      ],
    },
  ];
}

export function TrustModal({
  status,
  open,
  onClose,
}: {
  status: CopStatus;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  // Esc pops this overlay (top of the LIFO stack while it is open). The hook only
  // sits on the stack while open, so Esc reaches deeper levels when it is closed.
  useEscapeStack(onClose, open);

  const sel = status.selected;
  if (!open || !sel) return null;

  const sections = buildSections(sel, status.decrypt);
  const subject = `${String(sel.properties.name ?? sel.id)} · ${sel.kind}`;
  const granted = status.decrypt.state === "granted";
  const denied = status.decrypt.state === "denied";
  // Verdict pill shows ONLY a terminal KAS verdict. Without a live-KAS attempt
  // there is no key verdict to render (the pill is suppressed).
  const showPill = granted || denied;
  const verdict = granted ? "KEY RELEASED" : "KEY NOT HELD";

  return (
    <div
      className="trustmodal__scrim"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="trustmodal__card"
        role="dialog"
        aria-modal="true"
        aria-label="Trust and provenance"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="trustmodal__head">
          <div className="trustmodal__lock" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
          </div>
          <div className="trustmodal__titles">
            <div className="trustmodal__title">Trust &amp; provenance</div>
            <div className="trustmodal__subject">{subject}</div>
          </div>
          {showPill && (
            <span
              className={`trustmodal__pill trustmodal__pill--${granted ? "grant" : "deny"}`}
            >
              {verdict}
            </span>
          )}
          <button
            type="button"
            className="trustmodal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="trustmodal__body">
          {sections.map((s) => (
            <section className="trustmodal__section" key={s.title}>
              <div className="trustmodal__section-head">
                <span className="trustmodal__dot" aria-hidden="true" />
                <span className="trustmodal__section-title">{s.title}</span>
              </div>
              {s.rows.map((r) => (
                <div className="trustmodal__row" key={r.k}>
                  <span className="trustmodal__k">{r.k}</span>
                  <span className={`trustmodal__v trustmodal__v--${r.tone ?? "default"}`}>{r.v}</span>
                </div>
              ))}
            </section>
          ))}
          <p className="trustmodal__footnote">
            This is the &ldquo;cryptographic basement&rdquo; — the policy, keys, and signatures that
            travel inside the object itself. The picture on the glass is the result; this is the proof.
          </p>
        </div>
      </div>
    </div>
  );
}
